import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, Mail, MessageCircle, FileText, UserPlus, Loader2, Download, Eye, Pencil, Trash2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, API, waLink, getErr } from "../../lib/api";
import { statusPill } from "./AdminDashboard";
import DocumentViewer from "../../components/DocumentViewer";
import PortalCredentialsCard from "../../components/PortalCredentialsCard";
import EditRecordDialog from "../../components/EditRecordDialog";

const STATUSES = ["New", "Contacted", "In Progress", "Pending", "Completed", "Rejected"];

export default function AdminLeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState("");
  const [assignee, setAssignee] = useState("");
  const [operators, setOperators] = useState([]);
  const [converting, setConverting] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const onDelete = async () => {
    if (lead.converted_customer_id) {
      return toast.error("This lead is already converted. Delete the customer record instead if needed.");
    }
    if (!window.confirm(`Delete lead ${lead.ref} (${lead.full_name})? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/leads/${leadId}`);
      toast.success("Lead deleted");
      navigate("/admin/leads");
    } catch (e) { toast.error(getErr(e, "Delete failed")); }
    finally { setDeleting(false); }
  };

  const load = () => api.get(`/admin/leads/${leadId}`).then((r) => { setLead(r.data); setAssignee(r.data.assigned_to || ""); });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);
  useEffect(() => { api.get("/admin/operators").then((r) => setOperators(r.data.operators)).catch(() => {}); }, []);

  const updateStatus = async (status) => {
    try {
      await api.patch(`/admin/leads/${leadId}/status`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (e) { toast.error("Failed to update"); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/admin/leads/${leadId}/notes`, { text: note });
      setNote("");
      toast.success("Note added");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const saveAssignee = async () => {
    try {
      await api.patch(`/admin/leads/${leadId}/assign`, { assigned_to: assignee });
      toast.success("Assigned");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const convert = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(`/admin/leads/${leadId}/convert`);
      toast.success(`Converted as ${data.ref}`);
      navigate(`/admin/customers/${data.customer_id}`);
    } catch (e) {
      toast.error(getErr(e, "Conversion failed"));
    } finally { setConverting(false); }
  };

  const downloadFile = async (storage_path, filename) => {
    try {
      const res = await api.get(`/files/${storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  if (!lead) return <div className="p-10 text-[#5C6661]">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 md:p-10" data-testid="lead-detail-page">
      <Toaster richColors position="top-right" />

      <Link to="/admin/leads" className="text-sm text-[#5C6661] hover:text-[#121C17] inline-flex items-center gap-1 mb-6" data-testid="lead-detail-back">
        <ArrowLeft size={14} /> Back to leads
      </Link>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div>
          <span className="font-mono text-xs text-[#5C6661]">{lead.ref}</span>
          <h1 className="mt-1 text-3xl font-display font-medium tracking-tight text-[#121C17]" data-testid="lead-name">{lead.full_name}</h1>
          <p className="text-sm text-[#5C6661] mt-1">{lead.service_name} · ₹{lead.service_price.toLocaleString("en-IN")}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`tel:${lead.mobile}`} data-testid="lead-call" className="rounded-full px-4 py-2 text-sm font-medium border border-[#E5E2DC] bg-white inline-flex items-center gap-1 hover:bg-[#F3F1EC]"><Phone size={14} /> Call</a>
          <a href={waLink(`Hi ${lead.full_name}, regarding your ${lead.service_name} request (${lead.ref}).`)} target="_blank" rel="noopener noreferrer" data-testid="lead-whatsapp"
            className="rounded-full px-4 py-2 text-sm font-medium text-white inline-flex items-center gap-1" style={{ background: "#25D366" }}><MessageCircle size={14} /> WhatsApp</a>
          <a href={`mailto:${lead.email}`} data-testid="lead-email" className="rounded-full px-4 py-2 text-sm font-medium border border-[#E5E2DC] bg-white inline-flex items-center gap-1 hover:bg-[#F3F1EC]"><Mail size={14} /> Email</a>
          {!lead.converted_customer_id ? (
            <button onClick={convert} disabled={converting} data-testid="lead-convert"
              className="btn-primary rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-1 disabled:opacity-60">
              {converting ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Convert to customer
            </button>
          ) : (
            <Link to={`/admin/customers/${lead.converted_customer_id}`} data-testid="lead-view-customer"
              className="rounded-full px-4 py-2 text-sm font-medium bg-[#121C17] text-white inline-flex items-center gap-1">View Customer</Link>
          )}
          <button onClick={() => setEditOpen(true)} data-testid="lead-edit"
            className="rounded-full px-4 py-2 text-sm font-medium border border-[#E5E2DC] bg-white inline-flex items-center gap-1 hover:bg-[#F3F1EC]"><Pencil size={14} /> Edit</button>
          <button onClick={onDelete} disabled={deleting || !!lead.converted_customer_id} data-testid="lead-delete"
            title={lead.converted_customer_id ? "Lead is already converted — delete the customer instead" : "Delete lead"}
            className="rounded-full px-4 py-2 text-sm font-medium border border-red-200 text-red-700 bg-white inline-flex items-center gap-1 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed">
            {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Delete
          </button>
        </div>
      </div>

      <EditRecordDialog
        open={editOpen}
        kind="lead"
        record={lead}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); load(); }}
      />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status */}
          <div className="bento-card p-6" data-testid="lead-status-panel">
            <h3 className="label-eyebrow text-[10px]">Status</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => updateStatus(s)} data-testid={`status-${s.toLowerCase().replace(/\s/g, "-")}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    lead.status === s ? `${statusPill(s)} border-transparent font-semibold` : "bg-white text-[#5C6661] border-[#E5E2DC] hover:border-[#121C17]"
                  }`}>{s}</button>
              ))}
            </div>
          </div>

          {/* Customer details */}
          <div className="bento-card p-6" data-testid="lead-info-panel">
            <h3 className="label-eyebrow text-[10px]">Customer details</h3>
            <dl className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Info label="Mobile" value={lead.mobile} />
              <Info label="WhatsApp" value={lead.whatsapp} />
              <Info label="Email" value={lead.email} />
              <Info label="PAN" value={lead.pan} />
              <Info label="Aadhaar" value={lead.aadhaar} />
              <Info label="State" value={lead.state} />
              <Info label="District" value={lead.district} />
              <Info label="City" value={lead.city} />
              <Info label="PIN" value={lead.pincode} />
              <Info label="Created" value={new Date(lead.created_at).toLocaleString("en-IN")} />
              <div className="sm:col-span-2"><Info label="Address" value={lead.address} /></div>
              {lead.remarks && <div className="sm:col-span-2"><Info label="Remarks" value={lead.remarks} /></div>}
            </dl>
          </div>

          {/* Documents */}
          <div className="bento-card p-6" data-testid="lead-documents-panel">
            <h3 className="label-eyebrow text-[10px]">Documents ({lead.documents?.length || 0})</h3>
            {lead.documents?.length ? (
              <ul className="mt-4 space-y-2">
                {lead.documents.map((d) => (
                  <li key={d.id} className="flex items-center gap-3 p-3 rounded-xl border border-[#E5E2DC] hover:bg-[#FDFCFB]" data-testid={`document-${d.id}`}>
                    <FileText size={18} className="text-[#1E4D3B]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#121C17] truncate">{d.filename}</div>
                      <div className="text-xs text-[#5C6661]">{(d.size / 1024).toFixed(1)} KB · {d.content_type}</div>
                    </div>
                    <button onClick={() => setViewerDoc(d)} data-testid={`view-${d.id}`}
                      className="text-xs px-3 py-1.5 rounded-full border border-[#E5E2DC] hover:bg-[#F3F1EC] inline-flex items-center gap-1">
                      <Eye size={12} /> View
                    </button>
                    <button onClick={() => downloadFile(d.storage_path, d.filename)} data-testid={`download-${d.id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-[#121C17] text-white inline-flex items-center gap-1 hover:bg-[#1E4D3B]">
                      <Download size={12} /> Download
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-[#5C6661]">No documents uploaded.</p>
            )}
          </div>

          {/* Notes */}
          <div className="bento-card p-6" data-testid="lead-notes-panel">
            <h3 className="label-eyebrow text-[10px]">Internal notes</h3>
            <div className="mt-4 flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" data-testid="note-input"
                className="flex-1 bg-white border border-[#E5E2DC] rounded-xl px-4 py-2.5 text-sm focus:border-[#1E4D3B]" />
              <button onClick={addNote} data-testid="note-add" className="btn-primary rounded-xl px-4 py-2.5 text-sm font-medium">Add</button>
            </div>
            {lead.notes?.length ? (
              <ul className="mt-4 space-y-3">
                {lead.notes.slice().reverse().map((n) => (
                  <li key={n.id} className="p-3 rounded-xl bg-[#F3F1EC]">
                    <div className="text-sm text-[#121C17]">{n.text}</div>
                    <div className="text-xs text-[#5C6661] mt-1">{n.author} · {new Date(n.created_at).toLocaleString("en-IN")}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-[#5C6661]">No notes yet.</p>
            )}
          </div>
        </div>

        {/* RIGHT */}
        <div className="space-y-6">
          <PortalCredentialsCard kind="leads" id={lead.id} customerName={lead.full_name}
            whatsappTarget={lead.whatsapp || lead.mobile} portalUrl={`${window.location.origin}/login`} />

          <div className="bento-card p-6" data-testid="lead-assign-panel">
            <h3 className="label-eyebrow text-[10px]">Assigned to</h3>
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="assign-input"
              className="mt-3 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-2.5 text-sm focus:border-[#1E4D3B]">
              <option value="">— Unassigned —</option>
              {operators.map((op) => (
                <option key={op.id} value={op.id}>{op.name}{!op.is_active ? " (disabled)" : ""}</option>
              ))}
            </select>
            {lead.assigned_to_name && (
              <p className="mt-2 text-xs text-[#5C6661]">Currently: <span className="font-medium text-[#121C17]">{lead.assigned_to_name}</span></p>
            )}
            <button onClick={saveAssignee} data-testid="assign-save" className="mt-3 w-full btn-primary rounded-full py-2.5 text-sm font-medium">Save</button>
            {operators.length === 0 && (
              <p className="mt-3 text-xs text-[#5C6661]">No operators yet. Add team members in <Link to="/admin/operators" className="text-[#1E4D3B] underline">Operators</Link>.</p>
            )}
          </div>

          <div className="bento-card p-6" data-testid="lead-timeline-panel">
            <h3 className="label-eyebrow text-[10px]">Timeline</h3>
            <ol className="mt-4 space-y-3 border-l border-[#E5E2DC] pl-4">
              {lead.timeline?.slice().reverse().map((t) => (
                <li key={t.id} className="text-sm">
                  <div className="text-[#121C17]">{t.event}</div>
                  <div className="text-xs text-[#5C6661]">{new Date(t.at).toLocaleString("en-IN")}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      <DocumentViewer doc={viewerDoc} onClose={() => setViewerDoc(null)} />
    </div>
  );
}

const Info = ({ label, value }) => (
  <div>
    <dt className="text-xs label-eyebrow">{label}</dt>
    <dd className="mt-1 text-[#121C17]">{value || "—"}</dd>
  </div>
);
